import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import { useEmailLogs } from '@/hooks/useEmailLog'
import { movimientosKeys } from '@/hooks/useMovimientos'
import { movimientosService } from '@/services/movimientosService'
import { cuentaCorrienteService, LIMITE_AUDITORIA } from '@/services/cuentaCorrienteService'
import { armarLineaDeTiempo } from '@/lib/lineaDeTiempoCliente'

/** Techo de mails que lee la línea de tiempo. */
export const LIMITE_MAILS = 200

/** PostgREST devuelve a lo sumo estas filas por consulta, aunque no se pida un límite. */
const TECHO_POSTGREST = 1000

export const lineaDeTiempoKeys = {
  auditoria: (clienteId: string, verMovimientos: boolean) =>
    ['linea-tiempo-cliente', clienteId, 'auditoria', verMovimientos] as const,
}

/**
 * Línea de tiempo de un cliente: deudas, pagos, movimientos borrados, mails y cambios de estado.
 *
 * Sin `verMovimientos` no corre ninguna consulta: es parte de la cuenta corriente (ORR-613). Cada fuente falla por
 * separado: `fallas` dice cuál no cargó y `truncado` cuál llegó a su techo de filas.
 */
export function useLineaDeTiempoCliente(clienteId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const verMovimientos = !!puede.verMovimientos
  const listo = !!supabase && !supabaseLoading && !!clienteId && verMovimientos

  // Misma key que useMovimientosCliente(clienteId): comparten caché y las mutaciones de movimientos la invalidan.
  const movimientos = useQuery({
    queryKey: movimientosKeys.porCliente(clienteId, organizacionId, undefined, undefined),
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return movimientosService.listar(supabase!, organizacionId, { clienteId })
    },
    enabled: listo && verMovimientos && !!organizacionId,
  })

  const mails = useEmailLogs({
    clienteId,
    organizacionId: organizacionId ?? undefined,
    limit: LIMITE_MAILS,
  }, listo)

  const auditoria = useQuery({
    queryKey: lineaDeTiempoKeys.auditoria(clienteId, verMovimientos),
    queryFn: async () => {
      const [estados, eliminados] = await Promise.all([
        cuentaCorrienteService.obtenerEstadosCliente(supabase!, clienteId),
        verMovimientos ? cuentaCorrienteService.obtenerMovimientosEliminados(supabase!, clienteId) : [],
      ])
      return { estados, eliminados }
    },
    enabled: listo,
    staleTime: 0,
  })

  const eventos = useMemo(
    () =>
      armarLineaDeTiempo({
        movimientos: verMovimientos ? (movimientos.data ?? []) : [],
        mails: mails.data?.data ?? [],
        estados: auditoria.data?.estados ?? [],
        eliminados: auditoria.data?.eliminados ?? [],
      }),
    [verMovimientos, movimientos.data, mails.data, auditoria.data],
  )

  return {
    eventos,
    verMovimientos,
    cargando: movimientos.isLoading || mails.isLoading || auditoria.isLoading,
    fallas: {
      movimientos: movimientos.isError,
      mails: mails.isError,
      auditoria: auditoria.isError,
    },
    truncado: {
      movimientos: (movimientos.data?.length ?? 0) >= TECHO_POSTGREST,
      mails: (mails.data?.count ?? 0) > (mails.data?.data.length ?? 0),
      auditoria:
        (auditoria.data?.estados.length ?? 0) >= LIMITE_AUDITORIA ||
        (auditoria.data?.eliminados.length ?? 0) >= LIMITE_AUDITORIA,
    },
  }
}
